import { useCallback, useEffect, useMemo, useState } from "react";
import { fetchBookings, type Booking } from "@/lib/bookings";
import { listRows } from "@/lib/adminApi";
import type { Farm, Farmer, FarmerApplication, FarmerSettlement } from "@/lib/adminModules";
import type { Experience, Payment } from "@/lib/adminCommerce";
import { buildFarmPerformance, buildKpis } from "@/pages/admin/dashboard/dashboardData";
import DateRangeSelector, { type DateRangeKey } from "@/pages/admin/components/dashboard/DateRangeSelector";
import KpiGrid from "@/pages/admin/components/dashboard/KpiGrid";
import BookingsAnalytics from "@/pages/admin/components/dashboard/BookingsAnalytics";
import RevenueAnalytics from "@/pages/admin/components/dashboard/RevenueAnalytics";
import VisitorAnalytics from "@/pages/admin/components/dashboard/VisitorAnalytics";
import FarmPerformanceTable from "@/pages/admin/components/dashboard/FarmPerformanceTable";
import { ErrorBanner, LoadingState, PageHeader, btnGhost, btnPrimary } from "@/pages/admin/components/AdminUI";
import { exportToCsv, exportToPdf } from "@/pages/admin/exportUtils";
import { groupBookingsBySource, groupBookingsByType } from "@/lib/adminReports";

function isoDaysAgo(days: number): string {
  const date = new Date();
  date.setDate(date.getDate() - days);
  return `${date.getFullYear()}-${`${date.getMonth() + 1}`.padStart(2, "0")}-${`${date.getDate()}`.padStart(2, "0")}`;
}

function filterByRange(bookings: Booking[], range: DateRangeKey, start: string, end: string): Booking[] {
  const now = new Date();
  const dayMs = 86400000;
  const startOfDay = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
  let from: number | null = null;
  let to: number | null = null;
  if (range === "today") { from = startOfDay; to = startOfDay + dayMs; }
  else if (range === "yesterday") { from = startOfDay - dayMs; to = startOfDay; }
  else if (range === "7d") { from = startOfDay - 6 * dayMs; }
  else if (range === "30d") { from = startOfDay - 29 * dayMs; }
  else if (range === "month") { from = new Date(now.getFullYear(), now.getMonth(), 1).getTime(); }
  else if (range === "custom") {
    if (start) from = new Date(start).getTime();
    if (end) to = new Date(end).getTime() + dayMs;
  }
  return bookings.filter((booking) => {
    const time = new Date(booking.created_at).getTime();
    if (from !== null && time < from) return false;
    if (to !== null && time >= to) return false;
    return true;
  });
}

export default function AdminAnalytics() {
  const [bookings, setBookings] = useState<Booking[]>([]);
  const [farms, setFarms] = useState<Farm[]>([]);
  const [farmers, setFarmers] = useState<Farmer[]>([]);
  const [applications, setApplications] = useState<FarmerApplication[]>([]);
  const [settlements, setSettlements] = useState<FarmerSettlement[]>([]);
  const [experiences, setExperiences] = useState<Experience[]>([]);
  const [payments, setPayments] = useState<Payment[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [range, setRange] = useState<DateRangeKey>("30d");
  const [customStart, setCustomStart] = useState(isoDaysAgo(30));
  const [customEnd, setCustomEnd] = useState(isoDaysAgo(0));

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const [bookingRows, farmRows, farmerRows, appRows, settlementRows, experienceRows, paymentRows] =
        await Promise.all([
          fetchBookings(),
          listRows<Farm>("farms", "name"),
          listRows<Farmer>("farmers", "name"),
          listRows<FarmerApplication>("farmer_applications", "submitted_at"),
          listRows<FarmerSettlement>("farmer_settlements", "final_settlement"),
          listRows<Experience>("experiences", "name"),
          listRows<Payment>("payments", "paid_at"),
        ]);
      setBookings(bookingRows);
      setFarms(farmRows);
      setFarmers(farmerRows);
      setApplications(appRows);
      setSettlements(settlementRows);
      setExperiences(experienceRows);
      setPayments(paymentRows);
    } catch (err) {
      setError(err instanceof Error && err.message ? err.message : "We couldn't load analytics.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const kpis = useMemo(() => {
    const today = isoDaysAgo(0);
    const pendingSettlements = settlements
      .filter((s) => ["pending", "ready", "processing"].includes(s.status))
      .reduce((sum, s) => sum + Number(s.final_settlement || 0), 0);
    const todayRevenue = payments
      .filter((p) => p.status === "paid" && p.paid_at === today)
      .reduce((sum, p) => sum + Number(p.amount || 0), 0);
    return buildKpis({
      bookings,
      activeFarms: farms.filter((f) => f.status === "active").length,
      activeExperiences: experiences.filter((e) => e.status === "published").length,
      pendingApprovals: applications.filter((a) => a.stage !== "approved" && a.stage !== "rejected").length,
      pendingSettlements,
      todayRevenue,
    });
  }, [bookings, farms, experiences, applications, settlements, payments]);

  const farmRows = useMemo(() => buildFarmPerformance(farms, farmers, settlements), [farms, farmers, settlements]);
  const rangedBookings = useMemo(
    () => filterByRange(bookings, range, customStart, customEnd),
    [bookings, range, customStart, customEnd],
  );

  const exportRows = useMemo(() => {
    const byType = groupBookingsByType(rangedBookings);
    const bySource = groupBookingsBySource(rangedBookings);
    return [
      ...kpis.map((kpi) => ({ metric: kpi.label, value: kpi.value, group: "KPI" })),
      ...byType.map((row) => ({ metric: row.label, value: `${row.value}`, group: "Bookings by type" })),
      ...bySource.map((row) => ({ metric: row.label, value: `${row.value}`, group: "Bookings by source" })),
    ];
  }, [kpis, rangedBookings]);

  const columns = [
    { header: "Group", value: (row: { group: string }) => row.group },
    { header: "Metric", value: (row: { metric: string }) => row.metric },
    { header: "Value", value: (row: { value: string }) => row.value },
  ];

  return (
    <div>
      <PageHeader
        eyebrow="Reports"
        title="Analytics"
        subtitle="A single read on the whole business — bookings, visitors, revenue, farms and approvals, all from live data."
        actions={
          <>
            <button type="button" className={btnGhost} onClick={() => exportToCsv("rythugaru-analytics", columns, exportRows)}>
              <i className="ri-file-excel-2-line"></i> CSV
            </button>
            <button type="button" className={btnPrimary} onClick={() => exportToPdf({ title: "Analytics Summary", subtitle: "Live business snapshot", columns, rows: exportRows })}>
              <i className="ri-file-pdf-2-line"></i> PDF
            </button>
          </>
        }
      />

      <div className="flex flex-wrap items-center gap-3 mb-5">
        <DateRangeSelector
          value={range}
          onChange={setRange}
          customStart={customStart}
          customEnd={customEnd}
          onCustomChange={(start, end) => { setCustomStart(start); setCustomEnd(end); }}
        />
        <span className="text-sm text-foreground-500">
          {rangedBookings.length} bookings in range
        </span>
      </div>

      {error ? (
        <div className="mb-5">
          <ErrorBanner message={error} onRetry={load} onDismiss={() => setError("")} />
        </div>
      ) : null}

      {loading ? (
        <LoadingState label="Crunching your numbers…" />
      ) : (
        <div className="space-y-5 md:space-y-6">
          <KpiGrid cards={kpis} />
          <div className="grid grid-cols-1 xl:grid-cols-3 gap-5 md:gap-6">
            <div className="xl:col-span-2">
              <BookingsAnalytics bookings={rangedBookings} />
            </div>
            <VisitorAnalytics />
          </div>
          <RevenueAnalytics />
          <FarmPerformanceTable rows={farmRows} />
        </div>
      )}
    </div>
  );
}